<template>
  <!-- START : Profile Information section -->
  <WCForm
    :form="$v.form"
    @update:value="updateProfile"
    @update:valid="formUpdateValidCheck"
    class="w-100"
  >
    <h3>{{ $t('profilePersonalSection') }}</h3>
    <!-- Profile info - prefix -->
    <WCFormGroup class="wc-select" :field="$v.form.prefix" v-if="me.prefix && me.prefix.options">
      <WCLabel for="account-form__prefix">{{ $t('prefix') }}</WCLabel>
      <WCSelect class="bg-light" id="account-form__prefix" autocomplete="honorific-prefix">
        <option value=""></option>
        <option v-for="option in sortedPrefixOptions" :key="option.id" :value="option.id">{{
          option.description
        }}</option>
      </WCSelect>
      <WCValidationErrors />
    </WCFormGroup>
    <!-- Profile info - firstName -->
    <WCFormGroup class="wc-input" :field="$v.form.firstName" v-if="me.firstName">
      <WCLabel for="account-form__firstName">{{ $t('firstName') }}</WCLabel>
      <WCInput class="bg-light form-input" id="account-form__firstName" autocomplete="given-name" />
      <WCValidationErrors />
    </WCFormGroup>
    <!-- Profile info - middleName -->
    <WCFormGroup class="wc-input" :field="$v.form.middleName" v-if="me.middleName">
      <WCLabel for="account-form__middleName">{{ $t('middleName') }}</WCLabel>
      <WCInput
        class="bg-light form-input"
        id="account-form__middleName"
        autocomplete="additional-name"
      />
      <WCValidationErrors />
    </WCFormGroup>
    <!-- Profile info - lastName -->
    <WCFormGroup class="wc-input" :field="$v.form.lastName" v-if="me.lastName">
      <WCLabel for="account-form__lastName">{{ $t('lastName') }}</WCLabel>
      <WCInput class="bg-light form-input" id="account-form__lastName" autocomplete="family-name" />
      <WCValidationErrors />
    </WCFormGroup>
    <!-- Profile info - suffix -->
    <WCFormGroup class="wc-select" :field="$v.form.suffix" v-if="me.suffix && me.suffix.options">
      <WCLabel for="account-form__suffix">{{ $t('suffix') }}</WCLabel>
      <WCSelect class="bg-light" id="account-form__suffix" autocomplete="honorific-suffix">
        <option value=""></option>
        <option v-for="option in sortedSuffixOptions" :key="option.id" :value="option.id">{{
          option.description
        }}</option>
      </WCSelect>
      <WCValidationErrors />
    </WCFormGroup>
    <!-- Profile info - nickname -->
    <WCFormGroup class="wc-input" :field="$v.form.nickname" v-if="me.nickName">
      <WCLabel for="account-form__nickname">{{ $t('nickname') }}</WCLabel>
      <WCInput class="bg-light form-input" id="account-form__nickname" autocomplete="nickname" />
      <WCValidationErrors />
    </WCFormGroup>
    <!-- Profile info - birthDate -->
    <WCFormGroup class="wc-input" v-if="me.birthDate">
      <WCLabel for="account-form__birthDate">{{ $t('birthday') }}</WCLabel>
      <WCDateTimePicker
        datePlaceholder=" "
        dateInputId="account-form__birthDate"
        :dateFormat="datePickerFormat"
        :initialValue="initialBirthDate"
        :mode="datePickerMode"
        @onChange="onBirthDateChange"
        @onValidation="onBirthDateValidation"
        :minDate="birthMinDate"
        :maxDate="birthMaxDate"
        :fromDate="!form.birthDate ? fromDate : new Date(form.birthDate)"
        :requiredDate="birthdateRequiredCheck"
        :isBirthDate="true"
      />
      <WCValidationErrors :validation="birthDateErrorObj" class="pt-1 pl-1" />
    </WCFormGroup>
  </WCForm>
</template>

<script>
/* eslint-disable func-names */

import { maxLength, required } from 'vuelidate/lib/validators';
import WCFormGroup from '@/modules/forms/components/WCFormGroup/WCFormGroup.vue';
import WCLabel from '@/modules/forms/components/WCLabel/WCLabel.vue';
import WCInput from '@/modules/forms/components/WCInput/WCInput.vue';
import WCSelect from '@/modules/forms/components/WCSelect/WCSelect.vue';
import WCForm from '@/modules/forms/components/WCForm/WCForm.vue';
import WCValidationErrors from '@/modules/forms/components/WCValidationErrors/WCValidationErrors.vue';
import WCDateTimePicker from '@/components/WCDateTimePicker/WCDateTimePicker.vue';
import { FORMAT, MODE } from '@/constants/DatePickerConstants';
import { endOfDay, subYears } from 'date-fns';
import { minDate, maxDate } from '@/utils/validators/validators';
import { ACCOUNT_SETTINGS_OPTIONS } from '@/constants/AppConstants';
import { dynamicSort } from '@/utils';

export default {
  name: 'WCProfileInfoSectionForm',
  components: {
    WCFormGroup,
    WCLabel,
    WCInput,
    WCSelect,
    WCForm,
    WCValidationErrors,
    WCDateTimePicker,
  },
  emits: ['formUpdateValid', 'formChanged'],

  props: {
    me: {
      type: Object,
    },
  },
  data() {
    return {
      form: {
        prefix: this.me.prefix && this.me.prefix.value,
        firstName: this.me.firstName && this.me.firstName.value,
        middleName: this.me.middleName && this.me.middleName.value,
        lastName: this.me.lastName && this.me.lastName.value,
        suffix: this.me.suffix && this.me.suffix.value,
        nickname: this.me.nickName && this.me.nickName.value,
        birthDate: this.me.birthDate && this.me.birthDate.value,
      },
      birthDateErrorObj: {},
    };
  },

  validations() {
    const requiredString = {
      required,
      maxLength: maxLength(25),
    };

    return {
      form: {
        prefix:
          this.me.prefix && this.me.prefix.option === ACCOUNT_SETTINGS_OPTIONS.REQUIRED
            ? { required }
            : {},
        firstName:
          this.me.firstName && this.me.firstName.option === ACCOUNT_SETTINGS_OPTIONS.REQUIRED
            ? requiredString
            : { maxLength: maxLength(25) },
        middleName:
          this.me.middleName && this.me.middleName.option === ACCOUNT_SETTINGS_OPTIONS.REQUIRED
            ? requiredString
            : { maxLength: maxLength(25) },
        lastName:
          this.me.lastName && this.me.lastName.option === ACCOUNT_SETTINGS_OPTIONS.REQUIRED
            ? requiredString
            : { maxLength: maxLength(25) },
        suffix:
          this.me.suffix && this.me.suffix.option === ACCOUNT_SETTINGS_OPTIONS.REQUIRED
            ? { required }
            : {},
        nickname:
          this.me.nickName && this.me.nickName.option === ACCOUNT_SETTINGS_OPTIONS.REQUIRED
            ? requiredString
            : { maxLength: maxLength(25) },
        birthDate: this.birthDateValidator,
      },
    };
  },
  mounted() {
    this.formUpdateValidCheck();
  },
  methods: {
    /**
     * Method to emit validity of form fields
     */
    formUpdateValidCheck() {
      this.$emit(
        'formUpdateValid',
        !this.$v.$invalid && !this.birthDateErrorObj.$invalid,
        'profilePersonalSection',
      );
    },
    /**
     * Method to emit profile info form data if valid
     */
    updateProfile() {
      this.formUpdateValidCheck();
      if (!this.$v.$invalid && !this.birthDateErrorObj.$invalid) {
        this.$emit('formChanged', this.form);
      }
    },
    /**
     * Method to birthDate date on change
     */
    onBirthDateChange(event) {
      if (event.date && !this.birthDateErrorObj.$invalid) {
        const dateStringArr = event.dateString.split('/');
        const formattedDateString = `${dateStringArr[2]}-${dateStringArr[0]}-${dateStringArr[1]} 00:00:00`;
        this.form.birthDate = new Date(formattedDateString).toISOString().replace('.000Z', '');
      } else {
        this.form.birthDate = event.date;
      }
    },

    /**
     * Method to get birth date validation
     */
    onBirthDateValidation(event) {
      this.birthDateErrorObj = event.date;
      this.$v.form.birthDate.$touch();
      this.updateProfile();
    },
  },
  computed: {
    /**
     * Initial birth date to be displayed in input
     */
    initialBirthDate() {
      return this.me.birthDate.value ? new Date(`${this.me.birthDate.value} 00:00:00`) : '';
    },
    /**
     * Initial from date the calendar should be displayed
     */
    fromDate() {
      return endOfDay(subYears(new Date(), 30));
    },
    /**
     * To get birth min date
     */
    birthMinDate() {
      return endOfDay(subYears(new Date(), 150));
    },
    /**
     * To get birth max date
     */
    birthMaxDate() {
      return new Date();
    },
    /**
     * To get birthDate date picker format
     */
    datePickerFormat() {
      return FORMAT.DATE_DEFAULT;
    },
    /**
     * To get birthdate date picker mode
     */
    datePickerMode() {
      return MODE.DATE;
    },
    /**
     * Property to check if birthdate is required or not
     */
    birthdateRequiredCheck() {
      if (this.me.birthDate.option === ACCOUNT_SETTINGS_OPTIONS.REQUIRED) {
        return true;
      }
      return false;
    },
    /**
     * Birthdate validator composed based on required, optional and null
     */
    birthDateValidator() {
      let validator = {};
      const requiredBirthDate = {
        required,
        minDate: minDate(this.birthMinDate),
        maxDate: maxDate(this.birthMaxDate),
      };

      const optionalBirthDate = {
        minDate: minDate(this.birthMinDate),
        maxDate: maxDate(this.birthMaxDate),
      };
      if (this.form.birthDate && this.me.birthDate.option === ACCOUNT_SETTINGS_OPTIONS.REQUIRED) {
        validator = requiredBirthDate;
      } else if (
        this.form.birthDate &&
        this.me.birthDate.option === ACCOUNT_SETTINGS_OPTIONS.OPTIONAL
      ) {
        validator = optionalBirthDate;
      }
      return validator;
    },

    /**
     * Sort prefix options in ascending order
     */
    sortedPrefixOptions() {
      return this.me && this.me.prefix && dynamicSort(this.me.prefix.options, 'description');
    },

    /**
     * Sort suffix options in ascending order
     */
    sortedSuffixOptions() {
      return this.me && this.me.suffix && dynamicSort(this.me.suffix.options, 'description');
    },
  },
};
</script>
